import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { SnackbarService } from './_services/snackbar.service';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  // Denylist of genuinely invisible / dangerous control characters only.
  // We deliberately do NOT strip characters that have legitimate linguistic use
  // (combining marks, ZWJ/ZWNJ, non-breaking & ideographic spaces, ligatures,
  // typographic punctuation, variation selectors) - removing those corrupts
  // valid text in scripts like Vietnamese, Arabic, Persian, Indic and CJK, and
  // breaks emoji ZWJ sequences. See sanitize() below.
  private readonly forbiddenPatterns = [
    // Zero-width junk: ZERO WIDTH SPACE, WORD JOINER, BOM.
    // ZWNJ (U+200C) and ZWJ (U+200D) are intentionally excluded - they are
    // required by Persian, Arabic, Indic scripts and emoji ZWJ sequences.
    /[\u200B\u2060\uFEFF]/g,

    // Function application characters (invisible math operators)
    /[\u2061-\u2064]/g,

    // Control characters (C0/C1, excluding tab and newline)
    /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u0080-\u009F]/g,

    // Bidirectional controls - the actual "Trojan Source" spoofing threat
    /[\u200E\u200F\u202A-\u202E\u2066-\u2069\u061C]/g,

    // Line/paragraph separators
    /[\u2028\u2029]/g,

    // Special characters (object replacement, replacement char)
    /[\uFFFC\uFFFD]/g,

    // Newline characters
    /\r\n|\r|\n/g,

    // Backslash
    /\\/g,
  ];

  hasInvalidChars(str: string): boolean {
    return this.forbiddenPatterns.some((pattern) => pattern.test(str));
  }

  sanitize(str: string): string {
    // Do NOT use NFKD here: compatibility decomposition splits precomposed
    // accented letters (e.g. Vietnamese "ễ", "ứ") into a base letter + combining
    // marks, which were then stripped — corrupting valid text. Work on the raw
    // string and only compose to NFC at the end.
    let cleaned = str.replace(/\r\n|\r|\n/g, ' ');

    // Apply each pattern separately
    this.forbiddenPatterns.forEach((pattern) => {
      cleaned = cleaned.replace(pattern, '');
    });

    // Final normalization to composed form
    cleaned = cleaned.normalize('NFC');

    return cleaned;
  }
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private changes: ChangeDetectorRef,
    private snackbar: SnackbarService,
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === '\\') {
          const el = e.target as HTMLElement;
          if (
            el.tagName === 'INPUT' ||
            el.tagName === 'TEXTAREA' ||
            el.isContentEditable
          ) {
            e.preventDefault();
          }
        }
      });

      document.addEventListener(
        'input',
        (e: Event) => {
          const el = e.target as HTMLElement;
          if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
            const input = el as HTMLInputElement | HTMLTextAreaElement;
            if (input.value.includes('\\')) {
              const start = (input as HTMLInputElement).selectionStart ?? input.value.length;
              const removed = input.value.substring(0, start).split('\\').length - 1;
              const newValue = input.value.replace(/\\/g, '');
              input.value = newValue;
              const newCursor = Math.max(0, start - removed);
              if (this.canSetSelectionRange(input as HTMLInputElement)) {
                (input as HTMLInputElement).setSelectionRange(newCursor, newCursor);
              }
              input.dispatchEvent(new Event('input', { bubbles: true }));
              this.changes.detectChanges();
            }
          }
        },
        true,
      );

      document.addEventListener('paste', (e: ClipboardEvent) => {
        const el = e.target as HTMLElement;
        if (
          el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          (el as HTMLElement).isContentEditable
        ) {
          const pasted = e.clipboardData?.getData('text') || '';
          const sanitized = this.sanitize(pasted);
          const isModified = sanitized !== pasted;

          if (!isModified) {
            return;
          }

          if ('value' in el) {
            const input = el as HTMLInputElement | HTMLTextAreaElement;
            const selection = this.getSelectionRange(input);

            if (selection) {
              e.preventDefault();
              if (this.canSetSelectionRange(input)) {
                input.setRangeText(sanitized, selection.start, selection.end, 'end');
              } else {
                input.value =
                  input.value.substring(0, selection.start) +
                  sanitized +
                  input.value.substring(selection.end);
              }

              input.dispatchEvent(new Event('input', { bubbles: true }));
              this.changes.detectChanges();
            } else {
              setTimeout(() => {
                const cleanedValue = this.sanitize(input.value);
                if (cleanedValue !== input.value) {
                  input.value = cleanedValue;
                  input.dispatchEvent(new Event('input', { bubbles: true }));
                  this.changes.detectChanges();
                }
              });
            }
          } else if ((el as HTMLElement).isContentEditable) {
            e.preventDefault();
            (document as any).execCommand('insertText', false, sanitized);
            el.dispatchEvent(new Event('input', { bubbles: true }));
            this.changes.detectChanges();
          }

          if (this.snackbar) {
            this.snackbar.openSnackBar('Invalid characters has been removed.');
          }
        }
      });
    }
  }

  getSelectionRange(
    el: HTMLInputElement | HTMLTextAreaElement,
  ): { start: number; end: number } | null {
    try {
      const start = el.selectionStart;
      const end = el.selectionEnd;

      if (start === null || end === null) {
        return null;
      }

      return { start, end };
    } catch {
      return null;
    }
  }

  canSetSelectionRange(el: HTMLInputElement | HTMLTextAreaElement): boolean {
    return (
      el instanceof HTMLTextAreaElement ||
      ['text', 'search', 'tel', 'url', 'password'].includes(el.type)
    );
  }
}
